// Lo que cambia una subida, una línea por cosa (13-10-2026, Pau: «quiero ver exactamente qué se sube y qué cambia;
// en cada línea, preguntar al agente que la hizo y quitar esa cosa sin rechazar todo lo demás»). El contenido lo
// prepara el servidor (backend/src/despliegues/lineas.ts); aquí solo se enseña.
//
// Diseño (tras tres rechazos: ni barras, pastillas y colores por todas partes, ni una lista pelada sin antes → ahora):
// una lista agrupada, como los ajustes de un iPhone. Cada línea: qué cambia (título), «Antes» y «Ahora» en dos
// renglones, a quién toca en una frase, y sus dos acciones. El color solo aparece donde hay que mirar: «No sé a
// cuántos» (rojo), «No lo hablaste» (naranja) y lo que quitas.
import { ArrowUp, ChevronRight, CornerDownRight, MessageCircle, MinusCircle, RotateCcw, TriangleAlert, Users } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { formatClockTime } from '../../../lib/time';
import { mensajeError } from '../errores';
import { AreaTexto, Boton } from '../ui';
import type { Despliegue, Linea } from './api';
import { claveHilo, useBandejaStore } from './store';

/** «100 %», «10 €»: el número y su signo no se separan al partir la línea (en el móvil salía «(100» y «%)» abajo). */
export function sinCortes(texto: string): string {
  return texto.replace(/(\d) (%|€|\$)/g, '$1\u00a0$2');
}

/** Las líneas que salen si Pau quita `ids`: esas y las que arrastran (comparten trabajo o ajuste). */
export function lineasQueSalen(lineas: Linea[], ids: Iterable<string>): Set<string> {
  const porId = new Map(lineas.map((l) => [l.id, l]));
  const fuera = new Set<string>();
  for (const id of ids) {
    const l = porId.get(id);
    if (!l?.quitar) continue;
    fuera.add(id);
    for (const a of l.quitar.arrastra) fuera.add(a);
  }
  return fuera;
}

export function LineasSubida({
  d,
  lineas,
  sinEfecto,
  quitadas,
  alQuitar,
  alDevolver,
}: {
  d: Despliegue;
  lineas: Linea[];
  sinEfecto: Array<{ agente: string | null; titulo: string }>;
  /** Lo que Pau ha pulsado «Quitar» (sin las arrastradas). */
  quitadas: ReadonlySet<string>;
  alQuitar: (id: string) => void;
  alDevolver: (id: string) => void;
}) {
  const fuera = lineasQueSalen(lineas, quitadas);
  // Una sola línea abierta a la vez (preguntar o confirmar «Quitar»): la tarjeta no crece sin control.
  const [abierta, setAbierta] = useState<{ id: string; que: 'preguntar' | 'quitar' } | null>(null);
  const alternar = (id: string, que: 'preguntar' | 'quitar') => setAbierta((a) => (a?.id === id && a.que === que ? null : { id, que }));

  return (
    <section aria-label={t('oficinaV2.despliegues.lineas.aria')} className="space-y-3">
      <h3 className="px-1 text-o-sub font-semibold text-oficina-texto2">
        {t(lineas.length === 1 ? 'oficinaV2.despliegues.lineas.cambiaUna' : 'oficinaV2.despliegues.lineas.cambiaVarias', { n: lineas.length })}
      </h3>
      {lineas.length ? (
        <ul className="overflow-hidden rounded-o-l bg-white/[0.035] ring-1 ring-white/[0.07]">
          {lineas.map((l, i) => (
            <FilaLinea
              key={l.id}
              d={d}
              l={l}
              primera={i === 0}
              quitada={quitadas.has(l.id)}
              arrastradaPor={!quitadas.has(l.id) && fuera.has(l.id) ? lineas.find((o) => quitadas.has(o.id) && o.quitar?.arrastra.includes(l.id)) ?? null : null}
              arrastra={lineas.filter((o) => l.quitar?.arrastra.includes(o.id))}
              abierta={abierta?.id === l.id ? abierta.que : null}
              alPreguntar={() => alternar(l.id, 'preguntar')}
              alPedirQuitar={() => alternar(l.id, 'quitar')}
              alConfirmarQuitar={() => {
                alQuitar(l.id);
                setAbierta(null);
              }}
              alCancelar={() => setAbierta(null)}
              alDeshacer={() => alDevolver(l.id)}
            />
          ))}
        </ul>
      ) : (
        <p className="rounded-o-l bg-white/[0.035] px-4 py-4 text-o-cuerpo text-oficina-texto2 ring-1 ring-white/[0.07]">{t('oficinaV2.despliegues.lineas.ninguna')}</p>
      )}
      {sinEfecto.length ? <SinEfecto trabajos={sinEfecto} /> : null}
    </section>
  );
}

function FilaLinea({
  d,
  l,
  primera,
  quitada,
  arrastradaPor,
  arrastra,
  abierta,
  alPreguntar,
  alPedirQuitar,
  alConfirmarQuitar,
  alCancelar,
  alDeshacer,
}: {
  d: Despliegue;
  l: Linea;
  primera: boolean;
  quitada: boolean;
  arrastradaPor: Linea | null;
  arrastra: Linea[];
  abierta: 'preguntar' | 'quitar' | null;
  alPreguntar: () => void;
  alPedirQuitar: () => void;
  alConfirmarQuitar: () => void;
  alCancelar: () => void;
  alDeshacer: () => void;
}) {
  const borde = primera ? '' : 'border-t border-white/[0.07]';
  // Fuera de la subida: la línea se queda (para ver qué has quitado y deshacerlo), en una sola fila apagada.
  if (quitada || arrastradaPor) {
    return (
      <li className={cn('flex items-center gap-3 px-4 py-3.5 sm:px-5', borde)}>
        <MinusCircle className="h-[18px] w-[18px] shrink-0 text-oficina-texto3" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-o-cuerpo leading-snug text-oficina-texto3 line-through decoration-oficina-texto3/60">{l.titulo}</p>
          <p className="mt-0.5 text-o-sub text-oficina-texto3">
            {arrastradaPor ? t('oficinaV2.despliegues.lineas.saleCon', { titulo: arrastradaPor.titulo }) : t('oficinaV2.despliegues.lineas.noSube')}
          </p>
        </div>
        {quitada ? (
          <Boton variante="fantasma" tamano="sm" icono={RotateCcw} onClick={alDeshacer}>
            {t('oficinaV2.despliegues.lineas.deshacer')}
          </Boton>
        ) : null}
      </li>
    );
  }

  const quien = l.agente ?? d.pedidoPor?.nombre ?? d.pedidoPor?.titulo ?? null;
  const sePuedePreguntar = !!l.agentId || !!d.pedidoPor?.vivo;
  return (
    <li className={cn('px-4 py-4 sm:px-5 sm:py-[18px]', borde)}>
      <p className="text-o-titulo3 font-semibold leading-snug tracking-[-0.01em] text-oficina-texto">{sinCortes(l.titulo)}</p>

      <dl className="mt-2.5 grid grid-cols-[3.75rem_1fr] gap-x-3 gap-y-1.5 text-o-cuerpo leading-relaxed">
        {l.antes ? (
          <>
            <dt className="text-oficina-texto3">{t('oficinaV2.despliegues.lineas.antes')}</dt>
            <dd className="text-oficina-texto2">{sinCortes(l.antes)}</dd>
          </>
        ) : null}
        <dt className="text-oficina-texto3">{t(l.antes ? 'oficinaV2.despliegues.lineas.ahora' : 'oficinaV2.despliegues.lineas.nuevo')}</dt>
        <dd className="text-oficina-texto">{sinCortes(l.ahora)}</dd>
      </dl>

      {l.frases_nuevas.length ? (
        <figure className="mt-3 border-l-2 border-oficina-acento/50 pl-3">
          <figcaption className="text-o-sub text-oficina-texto3">{t('oficinaV2.despliegues.lineas.calypsoLeera')}</figcaption>
          {l.frases_nuevas.map((f) => (
            <blockquote key={f} className="mt-1 text-o-cuerpo italic leading-relaxed text-oficina-texto2">
              {f}
            </blockquote>
          ))}
        </figure>
      ) : null}

      <div className="mt-3 flex flex-col gap-1.5 text-o-texto leading-snug">
        <AQuien l={l} />
        <Hablado l={l} quien={quien} />
        {l.confianza === 'baja' ? <p className="text-oficina-texto3">{t('oficinaV2.despliegues.lineas.pocoSegura')}</p> : null}
      </div>

      {sePuedePreguntar || l.quitar ? (
        <div className="-mb-1 -ml-3 mt-2 flex flex-wrap gap-1">
          {sePuedePreguntar ? (
            <Boton variante="fantasma" tamano="sm" icono={MessageCircle} aria-expanded={abierta === 'preguntar'} onClick={alPreguntar} className={cn(abierta === 'preguntar' && 'bg-oficina-hover')}>
              {quien ? t('oficinaV2.despliegues.lineas.preguntarA', { quien }) : t('oficinaV2.despliegues.lineas.preguntar')}
            </Boton>
          ) : null}
          {l.quitar ? (
            <Boton variante="fantasma" tamano="sm" icono={MinusCircle} aria-expanded={abierta === 'quitar'} onClick={alPedirQuitar} className={cn('text-oficina-texto2 hover:text-oficina-texto', abierta === 'quitar' && 'bg-oficina-hover')}>
              {t('oficinaV2.despliegues.lineas.quitar')}
            </Boton>
          ) : null}
        </div>
      ) : null}

      {abierta === 'quitar' && l.quitar ? <ConfirmarQuitar l={l} arrastra={arrastra} alConfirmar={alConfirmarQuitar} alCancelar={alCancelar} /> : null}
      {abierta === 'preguntar' ? <PreguntarLinea d={d} l={l} quien={quien} /> : null}
    </li>
  );
}

function AQuien({ l }: { l: Linea }) {
  const tipo = l.a_quien.tipo;
  return (
    <p
      className={cn(
        'flex items-start gap-2',
        tipo === 'todos' && 'font-semibold text-oficina-texto',
        tipo === 'parte' && 'text-oficina-texto2',
        tipo === 'nadie' && 'text-oficina-texto3',
        tipo === 'desconocido' && 'font-semibold text-oficina-error',
      )}
    >
      {tipo === 'desconocido' ? <TriangleAlert className="mt-[1px] h-4 w-4 shrink-0" aria-hidden /> : <Users className="mt-[1px] h-4 w-4 shrink-0 opacity-80" aria-hidden />}
      <span>
        <span className="sr-only">{t('oficinaV2.despliegues.lineas.aQuien')} </span>
        {sinCortes(l.a_quien.texto)}
      </span>
    </p>
  );
}

/** Si Pau lo habló con el agente: solo se dice cuando no (para que lo mire) o cuando sí, con su frase. */
function Hablado({ l, quien }: { l: Linea; quien: string | null }) {
  if (l.hablado === 'no') {
    return (
      <p className="flex items-start gap-2 font-semibold text-oficina-bloqueado">
        <span className="mt-[6px] h-2 w-2 shrink-0 rounded-full bg-oficina-bloqueado" aria-hidden />
        {quien ? t('oficinaV2.despliegues.lineas.noHablado', { quien }) : t('oficinaV2.despliegues.lineas.noHabladoSinQuien')}
      </p>
    );
  }
  if (l.hablado === 'rechazado') {
    return (
      <p className="flex items-start gap-2 font-semibold text-oficina-error">
        <span className="mt-[6px] h-2 w-2 shrink-0 rounded-full bg-oficina-error" aria-hidden />
        <span>
          {t('oficinaV2.despliegues.lineas.rechazado')}
          {l.hablado_cita ? <span className="font-normal text-oficina-texto2"> {t('oficinaV2.despliegues.lineas.cita', { texto: l.hablado_cita })}</span> : null}
        </span>
      </p>
    );
  }
  if (l.hablado === 'si' && l.hablado_cita) {
    return (
      <p className="text-oficina-texto3">
        {t('oficinaV2.despliegues.lineas.lePediste')} <span className="italic">{t('oficinaV2.despliegues.lineas.cita', { texto: l.hablado_cita })}</span>
      </p>
    );
  }
  return null;
}

function ConfirmarQuitar({ l, arrastra, alConfirmar, alCancelar }: { l: Linea; arrastra: Linea[]; alConfirmar: () => void; alCancelar: () => void }) {
  const q = l.quitar!;
  return (
    <div className="mt-3 rounded-o-m bg-oficina-fondo/70 p-4 ring-1 ring-white/[0.08]">
      <p className="text-o-cuerpo font-semibold text-oficina-texto">{t('oficinaV2.despliegues.lineas.quitarTitulo')}</p>
      <ul className="mt-2 space-y-1.5 text-o-cuerpo leading-snug text-oficina-texto2">
        {q.trabajos.map((tr) => (
          <li key={tr.pr} className="flex gap-2">
            <CornerDownRight className="mt-[3px] h-4 w-4 shrink-0 text-oficina-texto3" aria-hidden />
            <span>{tr.agente ? t('oficinaV2.despliegues.lineas.trabajoDe', { titulo: tr.titulo, agente: tr.agente }) : `«${tr.titulo}»`}</span>
          </li>
        ))}
        {q.ajustes.length && !q.trabajos.length ? (
          <li className="flex gap-2">
            <CornerDownRight className="mt-[3px] h-4 w-4 shrink-0 text-oficina-texto3" aria-hidden />
            <span>{t(q.ajustes.length === 1 ? 'oficinaV2.despliegues.lineas.unAjuste' : 'oficinaV2.despliegues.lineas.variosAjustes', { n: q.ajustes.length })}</span>
          </li>
        ) : null}
      </ul>
      {arrastra.length ? (
        <p className="mt-3 text-o-texto leading-snug text-oficina-texto">
          {t(arrastra.length === 1 ? 'oficinaV2.despliegues.lineas.arrastraUna' : 'oficinaV2.despliegues.lineas.arrastraVarias')}{' '}
          {arrastra.map((a) => `«${a.titulo}»`).join(', ')}
        </p>
      ) : null}
      <p className="mt-3 text-o-texto leading-snug text-oficina-texto3">{t('oficinaV2.despliegues.lineas.quitarExplica')}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Boton tamano="sm" icono={MinusCircle} onClick={alConfirmar}>
          {t(arrastra.length ? 'oficinaV2.despliegues.lineas.quitarConArrastre' : 'oficinaV2.despliegues.lineas.quitarConfirmar', { n: arrastra.length + 1 })}
        </Boton>
        <Boton variante="fantasma" tamano="sm" onClick={alCancelar}>
          {t('oficinaV2.accion.cancelar')}
        </Boton>
      </div>
    </div>
  );
}

/** Repite `fn` cada `ms` mientras el componente esté en pantalla. */
function useCadaRato(fn: () => void, ms: number) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    ref.current();
    const id = setInterval(() => ref.current(), ms);
    return () => clearInterval(id);
  }, [ms]);
}

/** «Preguntar» en una línea: la conversación con el agente que la hizo, debajo de la propia línea. */
function PreguntarLinea({ d, l, quien }: { d: Despliegue; l: Linea; quien: string | null }) {
  const clave = claveHilo(d.runId, l.id);
  const hilo = useBandejaStore((s) => s.hilos[clave]) ?? [];
  const escribir = useBandejaStore((s) => s.escribir);
  const cargar = useBandejaStore((s) => s.cargarHilo);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Su respuesta llega a su conversación: se mira cada 5 s mientras esta línea está abierta.
  useCadaRato(() => void cargar(d.runId, l.id).catch(() => undefined), 5000);
  const fin = useRef<HTMLDivElement>(null);
  useEffect(() => {
    fin.current?.scrollIntoView({ block: 'nearest' });
  }, [hilo.length]);

  const enviar = async () => {
    const x = texto.trim();
    if (!x || enviando) return;
    setEnviando(true);
    setError(null);
    try {
      await escribir(d.runId, x, l.id);
      setTexto('');
    } catch (e) {
      setError(mensajeError(e));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="mt-3 rounded-o-m bg-oficina-fondo/70 p-3 ring-1 ring-white/[0.08] sm:p-4">
      <p className="text-o-sub leading-snug text-oficina-texto3">{quien ? t('oficinaV2.despliegues.lineas.hablasCon', { quien }) : t('oficinaV2.despliegues.lineas.hablasConQuienPidio')}</p>
      {hilo.length ? (
        <div className="mt-3 space-y-2">
          {hilo.map((m) => (
            <div key={m.id} className={cn('flex', m.de === 'pau' ? 'justify-end pl-[12%]' : 'justify-start pr-[12%]')}>
              <div className={cn('rounded-o-l px-3 py-1.5 text-o-cuerpo leading-[1.45]', m.de === 'pau' ? 'bg-oficina-burbuja-pau text-white' : 'bg-oficina-burbuja-agente text-oficina-texto ring-1 ring-white/[0.06]')}>
                {m.texto}
                <span className={cn('ml-2 text-o-mini', m.de === 'pau' ? 'text-oficina-meta-pau' : 'text-oficina-texto3')}>{formatClockTime(m.ts)}</span>
              </div>
            </div>
          ))}
          <div ref={fin} />
        </div>
      ) : null}
      <div className="mt-3 flex items-end gap-2">
        <AreaTexto
          data-autofoco
          rows={1}
          value={texto}
          maxLength={2000}
          placeholder={t('oficinaV2.despliegues.lineas.preguntaPlaceholder')}
          aria-label={t('oficinaV2.despliegues.lineas.preguntaAria', { titulo: l.titulo })}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void enviar();
            }
          }}
          className="min-h-10 flex-1"
        />
        <Boton variante="primario" icono={ArrowUp} aria-label={t('oficinaV2.chat.enviar')} disabled={!texto.trim()} cargando={enviando} onClick={() => void enviar()} className="w-10 px-0" />
      </div>
      {error ? <p className="mt-2 text-o-sub text-oficina-error">{error}</p> : null}
    </div>
  );
}

/** Lo que sube pero no cambia nada a los fans (informes, notas, la oficina): plegado, una frase cada uno. */
function SinEfecto({ trabajos }: { trabajos: Array<{ agente: string | null; titulo: string }> }) {
  return (
    <details className="group overflow-hidden rounded-o-l bg-white/[0.035] ring-1 ring-white/[0.07]">
      <summary className="flex min-h-[52px] cursor-pointer list-none items-center gap-3 px-4 py-3 text-o-cuerpo text-oficina-texto2 outline-none hover:text-oficina-texto focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/70 sm:px-5 [&::-webkit-details-marker]:hidden">
        <span className="min-w-0 flex-1">{t(trabajos.length === 1 ? 'oficinaV2.despliegues.lineas.sinEfectoUno' : 'oficinaV2.despliegues.lineas.sinEfectoVarios', { n: trabajos.length })}</span>
        <ChevronRight className="h-4 w-4 shrink-0 transition-transform group-open:rotate-90" aria-hidden />
      </summary>
      <ul className="space-y-2 px-4 pb-4 sm:px-5">
        {trabajos.map((s) => (
          <Renglon key={`${s.agente}-${s.titulo}`}>
            {s.agente ? <span className="text-oficina-texto">{s.agente} · </span> : null}
            {s.titulo}
          </Renglon>
        ))}
      </ul>
    </details>
  );
}

function Renglon({ children }: { children: ReactNode }) {
  return <li className="text-o-cuerpo leading-snug text-oficina-texto2">{children}</li>;
}
